import { useQueryClient } from 'react-query'
import {
  BatchProgramAttendeeOperation,
  MappedField,
  AttendeesApi,
  ProgramAttendeeCreateParams,
} from '@citruscamps/citrus-client'
import { useToast } from '../../../../../hooks/useToast'
import translations from '../../../../../translations/en.json'
import { generateApiConfig } from '../../../../../utils/client-config'
import { generateBaseKey } from '../../../../../utils/key-generator'
import { useRequestHandler } from '../../../../../hooks/useRequestHandler'

interface IProps {
  programId?: string
}

interface ImportAttendee {
  handleParseFile: (file: File) => Promise<MappedField[]>
  handleBulkCreate: (
    values: ProgramAttendeeCreateParams[],
  ) => Promise<BatchProgramAttendeeOperation>
}

export const useHandleAttendeeImport = ({ programId }: IProps): ImportAttendee => {
  const { requestHandler } = useRequestHandler()
  const { setToast } = useToast()
  const queryClient = useQueryClient()

  const handleParseFile = async (file: File): Promise<MappedField[]> => {
    if (programId) {
      const client = new AttendeesApi(generateApiConfig())
      const formData = new FormData()
      formData.set('file', file)
      const fields = await requestHandler(
        () =>
          client.parseFileProgramAttendee(
            { program_id: programId, file, purpose: 'other' },
            { body: formData },
          ),
        {
          toast: { setToast },
        },
      )
      return fields
    } else {
      setToast({
        header: (translations as any)['error.default_toast_header'],
        message: (translations as any)['error.default_toast_message'],
        type: 'danger',
      })
      return []
    }
  }

  const handleBulkCreate = async (
    values: ProgramAttendeeCreateParams[],
  ): Promise<BatchProgramAttendeeOperation> => {
    if (programId) {
      const client = new AttendeesApi(generateApiConfig())
      try {
        const operation = await requestHandler(() =>
          client.createBulkProgramAttendee({
            program_id: programId,
            ProgramAttendeeCreateParams: values,
          }),
        )
        await queryClient.invalidateQueries(
          generateBaseKey({
            type: 'program_attendee',
            program_id: programId,
          }),
        )
        return operation
      } catch (e: any) {
        setToast({
          header: (translations as any)['error.default_toast_header'],
          message: e?.message || (translations as any)['error.default_toast_message'],
          type: 'danger',
        })
        throw e
      }
    } else {
      setToast({
        header: (translations as any)['error.default_toast_header'],
        message: (translations as any)['error.default_toast_message'],
        type: 'danger',
      })
      throw new Error((translations as any)['error.default_toast_message'])
    }
  }

  return {
    handleParseFile,
    handleBulkCreate,
  }
}
